import { useEffect, useState, useMemo } from 'react';
import {
  PageHeader, Btn, Loading, Card, Badge, SegmentedControl, Select, Input, EmptyState, ErrorState, MetricCard,
} from '../ForBrands/ui/BrandUI';
import Icon from '../ForBrands/ui/icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  fetchAnalyticsOverview,
  fetchAnalyticsEvents,
  downloadAnalyticsEventsCsv,
} from './analyticsServices';

// KPI tile.
const Tile = ({ label, value, sub }) => (
  <MetricCard label={label} value={value ?? 0} sub={sub} />
);

const SectionTitle = ({ children, hint, right }) => (
  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
    <div className="flex items-baseline gap-2">
      <h2 className="bm-h-s text-bm-ink">{children}</h2>
      {hint && <span className="bm-body-s text-bm-muted">{hint}</span>}
    </div>
    {right}
  </div>
);

const TH = 'whitespace-nowrap border-b border-bm-border px-3 py-2.5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted';
const TD = 'px-3 py-2.5 bm-body-m text-bm-ink';

const fmtDt = (dt) => {
  if (!dt) return '';
  try {
    const d = new Date(dt);
    return d.toLocaleString();
  } catch {
    return String(dt);
  }
};

const AnalyticsTab = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpiCohort, setKpiCohort] = useState('live'); // 'live' | 'all' | 'pilot'

  const [filterType, setFilterType] = useState('');
  const [filterActor, setFilterActor] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAnalyticsOverview();
      setOverview(data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runFilter = async () => {
    setFilterLoading(true);
    try {
      const data = await fetchAnalyticsEvents({
        from: filterFrom || undefined,
        to:   filterTo || undefined,
        type: filterType || undefined,
        actor: filterActor || undefined,
        limit: 500,
      });
      setFilteredEvents(data.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Filter failed');
    } finally {
      setFilterLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      await downloadAnalyticsEventsCsv({
        from: filterFrom || undefined,
        to:   filterTo || undefined,
        type: filterType || undefined,
        actor: filterActor || undefined,
      });
      toast.success('Events CSV downloaded');
    } catch (err) {
      toast.error('Could not export events');
    }
  };

  const typeOptions = useMemo(() => {
    const set = new Set((overview?.typeBreakdown || []).map((t) => t.type));
    return Array.from(set);
  }, [overview]);

  const refresh = (
    <Btn variant="secondary" onClick={load} disabled={loading}>
      <Icon name="refresh" size={14} className="mr-1.5" /> Refresh
    </Btn>
  );

  if (loading) {
    return (
      <div className="w-full min-w-0 max-w-full pb-8">
        <PageHeader eyebrow="Admin Mode" title="Analytics" subtitle="Live event stream and aggregates." />
        <Loading label="Loading analytics..." />
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full min-w-0 max-w-full pb-8">
        <PageHeader eyebrow="Admin Mode" title="Analytics" subtitle="Live event stream and aggregates." actions={refresh} />
        <ErrorState>{error}</ErrorState>
        <div className="mt-4">
          <Btn variant="secondary" onClick={load}>Try again</Btn>
        </div>
      </div>
    );
  }

  const k = overview?.kpis || {};
  const f = overview?.funnel || {};
  const totals = overview?.totals || {};
  const top = overview?.topAthletes || [];
  const recent = overview?.recent || [];
  const breakdown = overview?.typeBreakdown || [];

  // Funnel percentages (signups → onboarded → coach-linked → cacheshare).
  const funnelRows = [
    { label: 'Signups',                value: f.signup },
    { label: 'Completed onboarding',   value: f.onboarded },
    { label: 'Linked to a coach',      value: f.verified },
    { label: 'Ran CacheShare',         value: f.cacheshareRun },
  ];
  const funnelMax = Math.max(...funnelRows.map((r) => r.value || 0), 1);

  return (
    <div className="w-full min-w-0 max-w-full space-y-6 pb-8">
      <PageHeader
        eyebrow="Admin Mode"
        title="Analytics"
        subtitle={<>Live event stream and aggregates. Generated {fmtDt(overview?.generatedAt)}.</>}
        actions={refresh}
      />

      {/* Population totals, split by cohort.
          Backend tags every existing pre-deploy doc as `pilot`; new
          signups land as `live`. The split makes it obvious how many of
          the headline numbers are real growth versus pilot legacy. */}
      <section>
        <SectionTitle hint="Live versus pilot">Population</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {[
            { label: 'Users',    obj: totals.users },
            { label: 'Athletes', obj: totals.athletes },
            { label: 'Coaches',  obj: totals.coaches },
          ].map(({ label, obj }) => {
            const all = obj?.all ?? 0;
            const live = obj?.live ?? 0;
            const pilot = obj?.pilot ?? 0;
            const verified = obj?.verified;
            return (
              <Card key={label} padding="p-5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="bm-overline">{label}</span>
                  <span className="bm-body-s text-bm-muted">{all} total</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-[8px] bg-bm-subtle px-3 py-2">
                    <div className="flex items-center gap-[6px] bm-label-xs text-bm-muted">
                      <span className="h-[7px] w-[7px] rounded-full bg-bm-positive" /> Live
                    </div>
                    <div className="bm-h-s text-bm-ink">{live}</div>
                  </div>
                  <div className="rounded-[8px] bg-bm-subtle px-3 py-2">
                    <div className="flex items-center gap-[6px] bm-label-xs text-bm-muted">
                      <span className="h-[7px] w-[7px] rounded-full bg-bm-tertiary" /> Pilot
                    </div>
                    <div className="bm-h-s text-bm-muted">{pilot}</div>
                  </div>
                </div>
                {verified !== undefined && (
                  <div className="mt-2 bm-body-s text-bm-muted">
                    Verified records on file: <span className="bm-label-xs text-bm-ink">{verified}</span>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      {/* Time-windowed KPIs, cohort-aware. */}
      <section>
        <SectionTitle
          right={(
            <SegmentedControl
              value={kpiCohort}
              onChange={setKpiCohort}
              options={[{ value: 'live', label: 'Live' }, { value: 'all', label: 'All' }, { value: 'pilot', label: 'Pilot' }]}
            />
          )}
        >
          Activity
        </SectionTitle>
        {(() => {
          const block = kpiCohort === 'live'  ? (k.live  || k)
                      : kpiCohort === 'pilot' ? (k.pilot || k)
                      : k;
          return (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Tile label="Signups (24h)"         value={block.signups?.['24h']}        sub={`${block.signups?.['7d']} this week · ${block.signups?.['30d']} this month`} />
              <Tile label="Profile views (24h)"   value={block.profileViews?.['24h']}   sub={`${block.profileViews?.['7d']} this week · ${block.profileViews?.['30d']} this month`} />
              <Tile label="CacheShare runs (24h)" value={block.cacheshareRuns?.['24h']} sub={`${block.cacheshareRuns?.['7d']} this week · ${block.cacheshareRuns?.['30d']} this month`} />
              <Tile label="Active users (DAU)"    value={block.activeUsers?.dau}        sub={`WAU ${block.activeUsers?.wau} · MAU ${block.activeUsers?.mau}`} />
            </div>
          );
        })()}
      </section>

      {/* Funnel */}
      <Card padding="p-5">
        <SectionTitle hint="Last 30 days">Conversion funnel</SectionTitle>
        <div className="space-y-3">
          {funnelRows.map((r, i) => {
            const pct = Math.round(((r.value || 0) / funnelMax) * 100);
            const fromPrev = i > 0
              ? funnelRows[i - 1].value
                ? Math.round(((r.value || 0) / funnelRows[i - 1].value) * 100)
                : 0
              : 100;
            return (
              <div key={r.label}>
                <div className="mb-1 flex justify-between gap-2">
                  <span className="bm-label-xs text-bm-ink">{r.label}</span>
                  <span className="bm-body-s text-bm-muted">
                    {r.value || 0}{i > 0 && <span className="ml-2 text-bm-tertiary">{fromPrev}% of step above</span>}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-bm-subtle">
                  <div className="h-full rounded-full bg-bm-orange" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Top viewed athletes */}
        <Card padding="p-0" className="overflow-hidden lg:col-span-2">
          <div className="px-5 pt-5">
            <SectionTitle hint="Last 30 days">Most viewed athletes</SectionTitle>
          </div>
          {top.length === 0 ? (
            <EmptyState className="mx-5 mb-5">No profile views recorded yet.</EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-bm-subtle">
                  <tr>
                    <th className={TH}>Athlete</th>
                    <th className={TH}>Status</th>
                    <th className={`${TH} text-right`}>Views</th>
                    <th className={`${TH} text-right`}>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map((a) => (
                    <tr key={a.email} className="border-b border-bm-border transition-colors last:border-0 hover:bg-bm-subtle">
                      <td className={TD}>
                        <button
                          type="button"
                          onClick={() => {
                            if (a.email) localStorage.setItem('athleteEmail', a.email);
                            navigate(`/business/athlette/profile/${a.athleteId || ''}`);
                          }}
                          className="flex items-center gap-2 text-left"
                        >
                          {a.avatar
                            ? <img src={a.avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
                            : <div className="h-7 w-7 rounded-full bg-bm-mutedbg" />}
                          <span className="bm-label-l text-bm-ink hover:text-bm-orange">{a.name || a.email}</span>
                        </button>
                      </td>
                      <td className={TD}>
                        {a.status === 'verified'
                          ? <Badge tone="outline" dot="#1f9d55">Verified</Badge>
                          : <Badge tone="attention">{a.status || 'unknown'}</Badge>}
                      </td>
                      <td className={`${TD} text-right bm-label-l`}>{a.views}</td>
                      <td className={`${TD} whitespace-nowrap text-right bm-body-s text-bm-muted`}>{fmtDt(a.lastSeen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* By-type breakdown */}
        <Card padding="p-5">
          <SectionTitle hint="Last 30 days">Events by type</SectionTitle>
          {breakdown.length === 0 ? (
            <EmptyState>No events yet.</EmptyState>
          ) : (
            <ul className="space-y-2">
              {breakdown.map((b) => {
                const pct = breakdown[0]?.count ? Math.round((b.count / breakdown[0].count) * 100) : 0;
                return (
                  <li key={b.type}>
                    <div className="flex justify-between gap-2">
                      <span className="truncate font-mono bm-body-s text-bm-muted">{b.type}</span>
                      <span className="bm-label-xs text-bm-ink">{b.count}</span>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-bm-subtle">
                      <div className="h-full rounded-full bg-bm-orange" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {/* Filtered events explorer */}
      <Card padding="p-0" className="overflow-hidden">
        <div className="px-5 pt-5">
          <SectionTitle
            hint="Most recent 50 events by default"
            right={(
              <Btn variant="secondary" size="sm" onClick={exportCsv}>
                <Icon name="download" size={13} className="mr-1.5" /> Export CSV
              </Btn>
            )}
          >
            Event explorer
          </SectionTitle>
          <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <Input type="datetime-local" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} aria-label="From" />
            <Input type="datetime-local" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} aria-label="To" />
            <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} aria-label="Event type">
              <option value="">Any type</option>
              {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
            <Input type="text" value={filterActor} onChange={(e) => setFilterActor(e.target.value)} placeholder="Actor email contains" aria-label="Actor" />
            <Btn onClick={runFilter} disabled={filterLoading}>
              {filterLoading ? 'Filtering...' : 'Apply filter'}
            </Btn>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-bm-subtle">
              <tr>
                <th className={TH}>Time</th>
                <th className={TH}>Type</th>
                <th className={TH}>Actor</th>
                <th className={TH}>Role</th>
                <th className={TH}>Target</th>
              </tr>
            </thead>
            <tbody>
              {(filteredEvents.length ? filteredEvents : recent).map((e, i) => (
                <tr key={`${e.ts}-${i}`} className="border-b border-bm-border transition-colors last:border-0 hover:bg-bm-subtle">
                  <td className={`${TD} whitespace-nowrap bm-body-s text-bm-muted`}>{fmtDt(e.ts)}</td>
                  <td className={`${TD} font-mono bm-body-s`}>{e.type}</td>
                  <td className={`${TD} bm-body-s`}>{e.actorEmail || <span className="text-bm-tertiary">guest</span>}</td>
                  <td className={`${TD} bm-body-s text-bm-muted`}>{(e.actorRole || '').replace('IS_', '')}</td>
                  <td className={`${TD} bm-body-s text-bm-muted`}>
                    {e.targetEmail || e.targetId || ''}
                    {e.targetType ? <span className="ml-1 text-bm-tertiary">({e.targetType})</span> : null}
                  </td>
                </tr>
              ))}
              {(filteredEvents.length === 0 && recent.length === 0) && (
                <tr><td colSpan={5} className="px-3 py-10 text-center bm-body-m text-bm-muted">No events.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AnalyticsTab;
